import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import LaunchBanner from "@/components/LaunchBanner";
import FilmCard from "@/components/FilmCard";
import { Input } from "@/components/ui/input";
import { Search, Filter } from "lucide-react";
import useGenres from "@/hooks/useGenres";
import SubGenreChips from "@/components/SubGenreChips";

const slug = (g) => g.toLowerCase().replace(/\s+/g, "-");

export default function Browse() {
  const { genres, ai_film: aiFilm } = useGenres();
  const GENRES = ["All", ...genres];
  const [searchParams] = useSearchParams();
  const [films, setFilms] = useState([]);
  const [genre, setGenre] = useState(searchParams.get("genre") || "All");
  const [subGenre, setSubGenre] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    const params = genre !== "All" ? { genre, ...(genre === aiFilm && subGenre ? { sub_genre: subGenre } : {}) } : {};
    api.get("/films", { params }).then((r) => setFilms(r.data || [])).catch(() => setFilms([]));
  }, [genre, subGenre, aiFilm]);

  const filtered = useMemo(() => {
    if (!q) return films;
    const s = q.toLowerCase();
    return films.filter((f) => f.title.toLowerCase().includes(s) || f.synopsis?.toLowerCase().includes(s));
  }, [films, q]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((f) => {
      map[f.genre] = map[f.genre] || [];
      map[f.genre].push(f);
    });
    return map;
  }, [filtered]);

  return (
    <div className="min-h-screen bg-ink grain">
      <LaunchBanner />
      <Navbar />
      <div className="pt-28 px-6 sm:px-12 lg:px-20 max-w-[1600px] mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <p className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-1">Library</p>
            <h1 className="font-display text-4xl sm:text-5xl font-black">Browse short films</h1>
          </div>
          <div className="relative w-full sm:w-80">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40" />
            <Input
              placeholder="Search titles, themes…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 bg-surface border-white/10 h-11"
              data-testid="browse-search-input"
            />
          </div>
        </div>

        <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-2 mb-8" data-testid="genre-filter-row">
          {GENRES.map((g) => (
            <button
              key={g}
              onClick={() => { setGenre(g); setSubGenre(null); }}
              className={`shrink-0 px-4 py-2 rounded-full text-sm transition-all border ${
                genre === g
                  ? "bg-terracotta border-terracotta text-white"
                  : "bg-surface border-white/10 text-foreground/70 hover:text-foreground hover:border-white/20"
              }`}
              data-testid={`genre-filter-${slug(g)}`}
            >
              {g}
            </button>
          ))}
        </div>
        {genre === aiFilm && (
          <div className="-mt-5 mb-8"><SubGenreChips value={subGenre} onChange={setSubGenre} testPrefix="browse-subgenre" /></div>
        )}

        {filtered.length === 0 ? (
          <div className="py-20 text-center" data-testid="browse-empty-state">
            <Filter className="w-10 h-10 mx-auto text-foreground/20 mb-3" />
            <p className="text-foreground/60">No films match your filters yet.</p>
          </div>
        ) : (
          <div className="space-y-12 pb-20">
            {Object.entries(grouped).map(([gen, list]) => (
              <div key={gen}>
                <h2 className="font-display text-xl font-bold mb-4 capitalize">{gen}</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                  {list.map((f, i) => (
                    <FilmCard key={f.id} film={f} index={i} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
